import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $computeTableMapSkipCellCheck,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableRowNode,
  getDOMCellFromTarget,
  getTableElement,
  TableNode,
} from '@lexical/table';
import { calculateZoomLevel, mergeRegister } from '@lexical/utils';
import { $getNearestNodeFromDOMNode, isHTMLElement, SKIP_SCROLL_INTO_VIEW_TAG } from 'lexical';

const MIN_ROW_HEIGHT = 33;
const MIN_COLUMN_WIDTH = 92;
const RESIZER_COLOR = '#1971c2';
// Approximate A4 page content width (794 - 72 - 72 margins) as fallback
// for new tables whose DOM isn't mounted yet at transform time.
const FALLBACK_PAGE_WIDTH = 650;

function TableCellResizer({ editor }) {
  const targetRef         = useRef(null);
  const resizerRef        = useRef(null);
  const tableRectRef      = useRef(null);
  const pointerStartPosRef = useRef(null);

  const [hasTable,           setHasTable]           = useState(false);
  const [activeCell,         updateActiveCell]       = useState(null);
  const [draggingDirection,  updateDraggingDirection] = useState(null);
  const [hoveredDirection,   updateHoveredDirection] = useState(null);
  const [pointerCurrentPos,  updatePointerCurrentPos] = useState(null);

  const resetState = useCallback(() => {
    updateActiveCell(null);
    targetRef.current = null;
    updateDraggingDirection(null);
    updateHoveredDirection(null);
    pointerStartPosRef.current = null;
    tableRectRef.current = null;
  }, []);

  // Track table existence + initialize colWidths for every table so the
  // colgroup/col-based layout is always active (required for setColWidths to work).
  useEffect(() => {
    const tableKeys = new Set();
    return mergeRegister(
      editor.registerMutationListener(TableNode, (nodeMutations) => {
        for (const [nodeKey, mutation] of nodeMutations) {
          if (mutation === 'destroyed') tableKeys.delete(nodeKey);
          else tableKeys.add(nodeKey);
        }
        setHasTable(tableKeys.size > 0);
      }),
      editor.registerNodeTransform(TableNode, (tableNode) => {
        if (tableNode.getColWidths()) return tableNode; // already set

        const numColumns = tableNode.getColumnCount();
        // At transform time the DOM may not be ready for new tables; fall back
        // to FALLBACK_PAGE_WIDTH so the table looks reasonable out of the box.
        const domEl   = editor.getElementByKey(tableNode.getKey());
        const tableEl = domEl
          ? (domEl.tagName === 'TABLE' ? domEl : domEl.querySelector('table'))
          : null;
        const tableWidth = tableEl ? tableEl.offsetWidth : 0;
        const perCol     = tableWidth > 0
          ? Math.max(MIN_COLUMN_WIDTH, Math.floor(tableWidth / numColumns))
          : Math.max(MIN_COLUMN_WIDTH, Math.floor(FALLBACK_PAGE_WIDTH / numColumns));

        tableNode.setColWidths(Array(numColumns).fill(perCol));
        return tableNode;
      }),
    );
  }, [editor]);

  // Register pointermove on the editor root to track which cell is hovered.
  useEffect(() => {
    if (!hasTable) return;

    const onPointerMove = (event) => {
      const target = event.target;
      if (!isHTMLElement(target)) return;

      // While dragging: just update pointer position for the live indicator.
      if (draggingDirection) {
        event.preventDefault();
        event.stopPropagation();
        updatePointerCurrentPos({ x: event.clientX, y: event.clientY });
        return;
      }

      // Ignore events on the resizer hit-zone divs themselves.
      if (resizerRef.current && resizerRef.current.contains(target)) return;

      // Only re-resolve the cell when the target element actually changed.
      if (targetRef.current !== target) {
        targetRef.current = target;
        const cell = getDOMCellFromTarget(target);
        if (cell && activeCell !== cell) {
          editor.getEditorState().read(
            () => {
              const tableCellNode = $getNearestNodeFromDOMNode(cell.elem);
              if (!tableCellNode) return;
              const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
              const tableElement = getTableElement(
                tableNode,
                editor.getElementByKey(tableNode.getKey()),
              );
              if (!tableElement) return;
              targetRef.current     = target;
              tableRectRef.current  = tableElement.getBoundingClientRect();
              updateActiveCell(cell);
            },
            { editor },
          );
        } else if (cell == null) {
          resetState();
        }
      }
    };

    // On touch: fire a synthetic move so the active cell is resolved on tap.
    const onPointerDown = (event) => {
      if (event.pointerType === 'touch') onPointerMove(event);
    };

    const resizerContainer = resizerRef.current;
    // Capture phase on the resizer so it sees moves even when pointer left root.
    resizerContainer?.addEventListener('pointermove', onPointerMove, { capture: true });

    // registerRootListener fires the callback immediately with the current root,
    // and again whenever the root changes. The returned cleanup is called when the
    // listener is unregistered (component unmount).
    const removeRootListener = editor.registerRootListener((rootElement) => {
      if (rootElement) {
        rootElement.addEventListener('pointermove', onPointerMove);
        rootElement.addEventListener('pointerdown', onPointerDown);
        return () => {
          rootElement.removeEventListener('pointermove', onPointerMove);
          rootElement.removeEventListener('pointerdown', onPointerDown);
        };
      }
    });

    return () => {
      removeRootListener();
      resizerContainer?.removeEventListener('pointermove', onPointerMove, { capture: true });
    };
  }, [activeCell, draggingDirection, editor, hasTable, resetState]);

  // ── Row height ──────────────────────────────────────────────────────────────

  const updateRowHeight = useCallback(
    (heightChange) => {
      if (!activeCell) return;
      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) return;

          const tableNode    = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const baseRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);
          const tableRows    = tableNode.getChildren();

          // For full-row-span merges apply to the first spanned row, otherwise
          // to the last (matching the playground's behaviour).
          const isFullRowMerge = tableCellNode.getColSpan() === tableNode.getColumnCount();
          const tableRowIndex  = isFullRowMerge
            ? baseRowIndex
            : baseRowIndex + tableCellNode.getRowSpan() - 1;

          if (tableRowIndex < 0 || tableRowIndex >= tableRows.length) return;
          const tableRow = tableRows[tableRowIndex];
          if (!$isTableRowNode(tableRow)) return;

          let height = tableRow.getHeight();
          if (height === undefined) {
            // No explicit height yet — read from DOM.
            const rowCells = tableRow.getChildren();
            height = Math.min(
              ...rowCells.map((cell) => {
                const domCell = editor.getElementByKey(cell.getKey());
                return domCell ? domCell.clientHeight : Infinity;
              }),
            );
          }

          tableRow.setHeight(Math.max(height + heightChange, MIN_ROW_HEIGHT));
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
    },
    [activeCell, editor],
  );

  // ── Column width ────────────────────────────────────────────────────────────

  const getCellColumnIndex = (tableCellNode, tableMap) => {
    for (let row = 0; row < tableMap.length; row++) {
      for (let col = 0; col < tableMap[row].length; col++) {
        if (tableMap[row][col].cell === tableCellNode) return col;
      }
    }
    return undefined;
  };

  const updateColumnWidth = useCallback(
    (widthChange) => {
      if (!activeCell) return;
      editor.update(
        () => {
          const tableCellNode = $getNearestNodeFromDOMNode(activeCell.elem);
          if (!$isTableCellNode(tableCellNode)) return;

          const tableNode  = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
          const [tableMap] = $computeTableMapSkipCellCheck(tableNode, null, null);
          const columnIndex = getCellColumnIndex(tableCellNode, tableMap);
          if (columnIndex === undefined) return;

          const colWidths = tableNode.getColWidths();
          if (!colWidths) return;
          const currentWidth = colWidths[columnIndex];
          if (currentWidth === undefined) return;

          const newColWidths = [...colWidths];
          newColWidths[columnIndex] = Math.max(currentWidth + widthChange, MIN_COLUMN_WIDTH);
          tableNode.setColWidths(newColWidths);
        },
        { tag: SKIP_SCROLL_INTO_VIEW_TAG },
      );
    },
    [activeCell, editor],
  );

  // ── Drag lifecycle ──────────────────────────────────────────────────────────

  const pointerUpHandler = useCallback(
    (direction) => {
      const handler = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!activeCell || !pointerStartPosRef.current) return;

        const { x, y } = pointerStartPosRef.current;
        const zoom = calculateZoomLevel(event.target);

        if (direction === 'bottom') {
          updateRowHeight((event.clientY - y) / zoom);
        } else {
          updateColumnWidth((event.clientX - x) / zoom);
        }

        resetState();
        document.removeEventListener('pointerup', handler);
      };
      return handler;
    },
    [activeCell, resetState, updateColumnWidth, updateRowHeight],
  );

  const toggleResize = useCallback(
    (direction) => (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (!activeCell) return;

      pointerStartPosRef.current = { x: event.clientX, y: event.clientY };
      updatePointerCurrentPos(pointerStartPosRef.current);
      updateDraggingDirection(direction);
      document.addEventListener('pointerup', pointerUpHandler(direction));
    },
    [activeCell, pointerUpHandler],
  );

  // ── Hit-zone positions ──────────────────────────────────────────────────────

  const getResizers = useCallback(() => {
    if (!activeCell) return { bottom: null, right: null };

    const { height, width, top, left } = activeCell.elem.getBoundingClientRect();
    const zoom      = calculateZoomLevel(activeCell.elem);
    const zoneSize  = 16; // px — width/height of the invisible drag zone

    // Base styles — transparent hit zones centred on the cell edge.
    // position:fixed so they track the viewport regardless of page scroll.
    const base = { position: 'fixed', zIndex: 99999, touchAction: 'none' };

    const styles = {
      right: {
        ...base,
        cursor:          'col-resize',
        backgroundColor: 'transparent',
        width:           `${zoneSize}px`,
        height:          `${height}px`,
        left:            `${left + width - zoneSize / 2}px`,
        top:             `${top}px`,
      },
      bottom: {
        ...base,
        cursor:          'row-resize',
        backgroundColor: 'transparent',
        height:          `${zoneSize}px`,
        width:           `${width}px`,
        left:            `${left}px`,
        top:             `${top + height - zoneSize / 2}px`,
      },
    };

    const tableRect = tableRectRef.current;

    if (draggingDirection && pointerCurrentPos && tableRect) {
      // During drag: collapse the zone into a 3px indicator that spans the table.
      if (draggingDirection === 'bottom') {
        styles.bottom.left            = `${tableRect.left}px`;
        styles.bottom.top             = `${pointerCurrentPos.y / zoom}px`;
        styles.bottom.height          = '3px';
        styles.bottom.width           = `${tableRect.width}px`;
        styles.bottom.backgroundColor = '#adf';
      } else {
        styles.right.top             = `${tableRect.top}px`;
        styles.right.left            = `${pointerCurrentPos.x / zoom}px`;
        styles.right.width           = '3px';
        styles.right.height          = `${tableRect.height}px`;
        styles.right.backgroundColor = '#adf';
      }
    } else if (!draggingDirection && hoveredDirection === 'right') {
      // Hover highlight: show a 2px blue line centred inside the 16px right zone.
      const halfZone       = zoneSize / 2;
      const lineW          = 2;
      const lineStart      = halfZone - lineW / 2;
      styles.right.backgroundImage = [
        `linear-gradient(90deg,`,
        `transparent ${lineStart}px,`,
        `${RESIZER_COLOR} ${lineStart}px,`,
        `${RESIZER_COLOR} ${lineStart + lineW}px,`,
        `transparent ${lineStart + lineW}px)`,
      ].join(' ');
      if (tableRect) {
        styles.right.top    = `${tableRect.top}px`;
        styles.right.height = `${tableRect.height}px`;
      }
    }

    return styles;
  }, [activeCell, draggingDirection, hoveredDirection, pointerCurrentPos]);

  // ── Render ──────────────────────────────────────────────────────────────────

  const handlePointerEnter = useCallback(
    (direction) => () => {
      if (!draggingDirection) updateHoveredDirection(direction);
    },
    [draggingDirection],
  );

  const handlePointerLeave = useCallback(() => {
    if (!draggingDirection) updateHoveredDirection(null);
  }, [draggingDirection]);

  const resizerStyles = getResizers();

  return (
    <div ref={resizerRef}>
      {activeCell != null && (
        <>
          <div
            style={resizerStyles.right || undefined}
            onPointerEnter={handlePointerEnter('right')}
            onPointerLeave={handlePointerLeave}
            onPointerDown={toggleResize('right')}
          />
          <div
            style={resizerStyles.bottom || undefined}
            onPointerDown={toggleResize('bottom')}
          />
        </>
      )}
    </div>
  );
}

export default function TableCellResizerPlugin() {
  const [editor]   = useLexicalComposerContext();
  const isEditable = useLexicalEditable();

  return useMemo(
    () =>
      isEditable
        ? createPortal(<TableCellResizer editor={editor} />, document.body)
        : null,
    [editor, isEditable],
  );
}
